import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";

export interface ClientPickerValue {
  clientId: number;
  fullName: string;
  emailAddress: string | null;
  companyId: number | null;
  companyName: string | null;
}

export interface ClientPickerProps {
  value: ClientPickerValue | null;
  onChange: (v: ClientPickerValue | null) => void;
  placeholder?: string;
}

export function ClientPicker({ value, onChange, placeholder }: ClientPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["client-finder", q],
    queryFn: () =>
      apiFetch<ClientPickerValue[]>(
        `/api/clients/finder?q=${encodeURIComponent(q)}&take=10`,
      ),
    staleTime: 1500,
  });

  // Sync external value changes to local input state
  useEffect(() => {
    if (value && q !== value.fullName) {
      setQ(value.fullName);
    } else if (!value && q.length > 0) {
      setQ("");
    }
  }, [value?.clientId]); // Only run when actual client selection changes

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <Input
            value={q}
            onChange={(e) => {
              const val = e.target.value;
              setQ(val);
              if (value && val !== value.fullName) {
                onChange(null);
              }
              if (!open) setOpen(true);
            }}
            onClick={() => {
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              if (q.length > 0) setOpen(true);
            }}
            placeholder={placeholder ?? "Select client..."}
            className={cn("w-full pr-8", !value && q === "" ? "text-muted-foreground" : "")}
          />
          <ChevronsUpDown className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground opacity-50 pointer-events-none" />
        </div>
      </PopoverAnchor>
      <PopoverContent 
        className="p-0" 
        style={{ width: "var(--radix-popover-trigger-width)" }}
        align="start" 
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {isLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">Searching...</div>
        )}
        {!isLoading && !data && (
          <div className="py-6 text-center text-sm text-muted-foreground">Start typing to search clients...</div>
        )}
        {!isLoading && data && data.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">No matches</div>
        )}
        {data && data.length > 0 && (
          <div className="flex max-h-[300px] flex-col overflow-y-auto">
            {data.map((r) => (
              <button
                key={r.clientId}
                type="button"
                className="flex w-full items-center rounded-sm px-2 py-2 text-sm hover:bg-slate-100 hover:text-slate-900 text-left transition-colors"
                onClick={() => {
                  onChange(r);
                  setQ(r.fullName);
                  setOpen(false);
                }}
              >
                <span>{r.fullName}</span>
                {r.companyName && (
                  <span className="ml-2 text-muted-foreground">({r.companyName})</span>
                )}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
